import { useState } from 'react';
import { LocaleLink } from '../../../i18n/locale-link';
import type { PlanCard } from '../../../content/landings/types';
import { LandingSection } from '../layout/landing-section';

interface Props {
  title: string;
  lead: string;
  periodMonthly: string;
  periodYearly: string;
  periodYearlyBadge: string;
  plans: PlanCard[];
  payNote: string;
  onCtaClick: (label: string) => void;
  /**
   * Ссылка-переход на лендинг `/ip` — печатается ОДНОЙ строкой под всей
   * сеткой тарифов (отступление 11, 2026-08-20: раньше жила под первой
   * карточкой, «ИП»/«ЯTT», пока та не была удалена из plans[]). Единственный
   * переход между двумя лендингами, см. докстринг `StartContent.ipCrossLinkText`.
   * Необязательные пропы: `pricing-three.test.tsx` не передаёт их вовсе.
   */
  ipCrossLinkText?: string;
  ipCrossLinkLabel?: string;
}

const FEATURE_ITEM_CLS =
  "relative pl-[25px] text-[14.5px] before:absolute before:left-0 before:font-extrabold before:text-status-active-text before:content-['✓']";

/**
 * Три тарифа с переключателем периода.
 *
 * ⚠️ Переключатель меняет ВИДИМОСТЬ (CSS), а не состав разметки. На `/plans`
 * ровно такой же по виду переключатель был сделан условным рендерингом и
 * срезал крауляемый ценовой текст с 285 слов до 146, а число цен с 5 до 2 —
 * страница пререндерится, поэтому «спрятать» и «удалить» здесь не синонимы.
 */
export function PricingThree({
  title,
  lead,
  periodMonthly,
  periodYearly,
  periodYearlyBadge,
  plans,
  payNote,
  onCtaClick,
  ipCrossLinkText,
  ipCrossLinkLabel,
}: Props) {
  const [yearly, setYearly] = useState(false);
  const hide = 'absolute h-px w-px overflow-hidden opacity-0';

  return (
    <LandingSection id="pricing" top="sm">
      <h2 className="mb-3 text-[clamp(26px,4.2vw,38px)] font-extrabold leading-[1.12] tracking-[-0.02em]">
        {title}
      </h2>
      <p className="max-w-[680px] text-[clamp(16.5px,2.2vw,19px)] text-ink-soft">{lead}</p>

      {/* `max-w-full` + `flex-wrap` на самом переключателе: `inline-flex` без
          них выпускал «Год ×2 выгода» за правый край экрана телефона. Боковые
          отступы кнопок на мобильном срезаны — этого хватает, чтобы пилюля
          уложилась целиком, не разрываясь на две строки. */}
      <div className="my-5 inline-flex max-w-full flex-wrap gap-1 rounded-full border border-edge bg-brand-light p-1 sm:flex-nowrap">
        <button
          type="button"
          onClick={() => setYearly(false)}
          aria-pressed={!yearly}
          className={`min-h-[44px] rounded-full px-4 text-[14.5px] font-extrabold sm:min-h-0 sm:px-5 sm:py-[9px] ${!yearly ? 'bg-bone-surface text-brand shadow-sm' : 'text-ink-soft'}`}
        >
          {periodMonthly}
        </button>
        <button
          type="button"
          onClick={() => setYearly(true)}
          aria-pressed={yearly}
          className={`inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 text-[14.5px] font-extrabold sm:min-h-0 sm:px-5 sm:py-[9px] ${yearly ? 'bg-bone-surface text-brand shadow-sm' : 'text-ink-soft'}`}
        >
          {periodYearly}
          <span className="whitespace-nowrap rounded-full bg-brand px-2 py-[3px] text-[10.5px] font-extrabold tracking-[0.04em] text-white">
            {periodYearlyBadge}
          </span>
        </button>
      </div>

      {/* Отступление 11 (2026-08-20): /start больше не всегда передаёт три
          тарифа (ИП/ЯTT убран — см. start.ru.ts) — сетка подстраивается под
          фактическое число карточек, а не жёстко держит 3 колонки. При двух
          карточках `lg:grid-cols-2` центрируется и не тянется во всю ширину
          `max-w-[1120px]`, иначе Pro/Бизнес расползлись бы неоправданно широко. */}
      <div
        className={`mt-6 grid gap-[18px] lg:items-start ${
          plans.length === 2 ? 'lg:mx-auto lg:max-w-[760px] lg:grid-cols-2' : 'lg:grid-cols-3'
        }`}
      >
        {plans.map((p) => {
          const hasYearly = p.priceYearly != null;
          // Тариф без годовой цены (раньше — «ИП»/«ЯTT»; отступление 11,
          // 2026-08-20: такого тарифа в plans[] /start больше нет, но
          // компонент остаётся общим для любого набора карточек) не имеет
          // data-y-аналога: месячная цена остаётся видимой при любом
          // положении переключателя, как и в исходном макете.
          const hideMonthly = hasYearly && yearly;
          return (
            // `min-w-0` on BOTH this item and the card below: each is itself
            // a grid (this one nested one level deeper), and a grid item's
            // default `min-width: auto` lets its content (the nowrap price
            // below) blow the item wider than its track — measured on prod
            // at 1440px: Pro rendered 367.66px in a 348px track, Бизнес
            // overhung the page's content edge by up to 32.2px in «Год».
            <div key={p.name} className="grid min-w-0 gap-2.5">
              <div
                className={`relative min-w-0 rounded-2xl bg-bone-surface p-[26px] shadow-sm ${p.highlighted ? 'border-2 border-brand shadow-lg' : 'border border-edge'}`}
              >
                {p.flag && (
                  <span className="absolute -top-[13px] right-5 rounded-full bg-brand px-3 py-[5px] text-[11.5px] font-extrabold uppercase tracking-[0.06em] text-white">
                    {p.flag}
                  </span>
                )}
                <h3 className="mb-1.5 text-[18px] font-extrabold leading-[1.12]">{p.name}</h3>

                {/* Обе цены всегда в DOM; переключатель прячет одну классом. */}
                <div
                  className={
                    hideMonthly
                      ? hide
                      : 'whitespace-nowrap text-[30px] font-extrabold leading-[1.1] sm:text-4xl'
                  }
                >
                  {p.priceMonthly}
                </div>
                {hasYearly && (
                  <div
                    className={
                      yearly
                        ? 'whitespace-nowrap text-[30px] font-extrabold leading-[1.1] sm:text-4xl'
                        : hide
                    }
                  >
                    {p.wasYearly && (
                      <span className="block text-sm font-semibold text-ink-muted line-through">
                        {p.wasYearly}
                      </span>
                    )}
                    {p.priceYearly}
                  </div>
                )}
                <div
                  className={yearly ? hide : 'mt-1.5 text-[13px] font-bold text-status-active-text'}
                >
                  {p.noteMonthly}
                </div>
                <div
                  className={yearly ? 'mt-1.5 text-[13px] font-bold text-status-active-text' : hide}
                >
                  {p.noteYearly}
                </div>

                <ul className="my-[18px] grid list-none gap-[9px]">
                  {p.features.map((f) => (
                    <li key={f} className={FEATURE_ITEM_CLS}>
                      {f}
                    </li>
                  ))}
                  {p.yearlyOnlyFeatures.map((f) => (
                    <li key={f} className={yearly ? FEATURE_ITEM_CLS : hide}>
                      {f}
                    </li>
                  ))}
                </ul>

                {p.note && <p className="mb-4 text-[13.5px] text-ink-soft">{p.note}</p>}

                <LocaleLink
                  to="/plans"
                  onClick={() => onCtaClick(`plan-${p.name.toLowerCase()}`)}
                  className={`block w-full rounded-lg px-6 py-3.5 text-center text-base font-bold no-underline ${p.highlighted ? 'border-[1.5px] border-brand bg-brand text-white' : 'bg-brand-light text-brand'}`}
                >
                  {p.cta}
                </LocaleLink>
              </div>
            </div>
          );
        })}
      </div>
      {/* Отступление 11 (2026-08-20): раньше печаталась ТОЛЬКО под первой
          карточкой (в старых данных — «ИП»/«ЯTT»). После её удаления из
          plans[] переход на /ip больше не привязан ни к одной карточке —
          теперь это отдельная строка под всей сеткой тарифов, единственный
          мост между /start и /ip (см. докстринг `StartContent.ipCrossLinkText`). */}
      {ipCrossLinkText && ipCrossLinkLabel && (
        <p className="mt-[18px] text-[13.5px] text-ink-soft">
          {ipCrossLinkText}{' '}
          <LocaleLink to="/ip" className="font-semibold text-brand">
            {ipCrossLinkLabel}
          </LocaleLink>
        </p>
      )}
      <p className="mt-[18px] text-[13.5px] text-ink-soft">{payNote}</p>
    </LandingSection>
  );
}
